/**
 * Dataset loader utility for the ML playground
 */

export interface Dataset {
  name: string;
  description: string;
  filename: string;
  features: string[];
  target: string;
  size: string;
  type: 'classification' | 'regression';
}

export const AVAILABLE_DATASETS: Dataset[] = [
  {
    name: "Iris Flowers",
    description: "Classic dataset for flower classification based on petal/sepal measurements",
    filename: "iris.csv",
    features: ["sepal_length", "sepal_width", "petal_length", "petal_width"],
    target: "variety",
    size: "150 samples",
    type: "classification"
  },
  {
    name: "Wine Classification", 
    description: "Chemical analysis of wines from different cultivars",
    filename: "wine.csv",
    features: ["alcohol", "malic_acid", "ash", "alcalinity_of_ash", "magnesium", "total_phenols", "flavanoids", "nonflavanoid_phenols", "proanthocyanins", "color_intensity", "hue", "od280_od315_of_diluted_wines", "proline"],
    target: "class",
    size: "178 samples",
    type: "classification"
  },
  {
    name: "Diabetes Prediction",
    description: "Predict diabetes onset based on medical diagnostic measurements",
    filename: "diabetes.csv", 
    features: ["pregnancies", "glucose", "blood_pressure", "skin_thickness", "insulin", "bmi", "diabetes_pedigree", "age"],
    target: "outcome",
    size: "768 samples",
    type: "classification"
  },
  {
    name: "Boston Housing",
    description: "Predict house prices based on various features",
    filename: "boston_housing.csv",
    features: ["crim", "zn", "indus", "chas", "nox", "rm", "age", "dis", "rad", "tax", "ptratio", "b", "lstat"],
    target: "medv",
    size: "506 samples", 
    type: "regression"
  }
];

export class DatasetLoader {
  static async loadDataset(filename: string): Promise<{
    success: boolean;
    data?: { X: number[][], y: (number | string)[], feature_names: string[], target_name: string };
    error?: string;
  }> {
    try {
      const response = await fetch(`/datasets/${filename}`);
      if (!response.ok) {
        throw new Error(`Failed to load dataset: ${response.statusText}`);
      }
      
      const csvText = await response.text();
      const lines = csvText.trim().split('\n');
      
      if (lines.length === 0) {
        throw new Error('Dataset is empty');
      }

      // Get dataset info
      const dataset = AVAILABLE_DATASETS.find(d => d.filename === filename);
      if (!dataset) {
        throw new Error('Dataset configuration not found');
      }

      // Parse CSV based on dataset type
      let parsedData;
      if (filename === 'wine.csv') {
        parsedData = this.parseWineDataset(lines, dataset);
      } else if (filename === 'iris.csv') {
        parsedData = this.parseIrisDataset(lines, dataset);
      } else if (filename === 'diabetes.csv') {
        parsedData = this.parseDiabetesDataset(lines, dataset);
      } else if (filename === 'boston_housing.csv') {
        parsedData = this.parseBostonHousingDataset(lines, dataset);
      } else {
        throw new Error('Unsupported dataset format');
      }

      return {
        success: true,
        data: parsedData
      };
    } catch (error) {
      console.error('Error loading dataset:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load dataset'
      };
    }
  }

  private static parseWineDataset(lines: string[], dataset: Dataset) {
    const X: number[][] = [];
    const y: number[] = [];
    
    lines.forEach(line => {
      const values = line.split(',').map(v => parseFloat(v.trim()));
      if (values.length >= 14) {
        y.push(values[0]); // First column is class
        X.push(values.slice(1)); // Rest are features
      }
    });

    return {
      X,
      y,
      feature_names: dataset.features,
      target_name: dataset.target
    };
  }

  private static parseIrisDataset(lines: string[], dataset: Dataset) {
    const X: number[][] = [];
    const y: string[] = [];
    
    // Skip header if present
    const dataLines = lines[0].includes('sepal') ? lines.slice(1) : lines;
    
    dataLines.forEach(line => {
      const values = line.split(',').map(v => v.trim());
      if (values.length >= 5) {
        const features = values.slice(0, 4).map(v => parseFloat(v));
        if (!features.some(isNaN)) {
          X.push(features);
          y.push(values[4]); // Species name
        }
      }
    });

    return {
      X,
      y,
      feature_names: dataset.features,
      target_name: dataset.target
    };
  }

  private static parseDiabetesDataset(lines: string[], dataset: Dataset) {
    const X: number[][] = [];
    const y: number[] = [];
    
    lines.forEach(line => {
      const values = line.split(',').map(v => parseFloat(v.trim()));
      if (values.length >= 9 && !values.some(isNaN)) {
        X.push(values.slice(0, 8)); // First 8 are features
        y.push(values[8]); // Last is target
      }
    });

    return {
      X,
      y,
      feature_names: dataset.features,
      target_name: dataset.target
    };
  }

  private static parseBostonHousingDataset(lines: string[], dataset: Dataset) {
    const X: number[][] = [];
    const y: number[] = [];
    
    // Skip header if present
    const dataLines = lines[0].includes('crim') ? lines.slice(1) : lines;
    
    dataLines.forEach(line => {
      const values = line.split(',').map(v => parseFloat(v.trim()));
      if (values.length >= 14 && !values.some(isNaN)) {
        X.push(values.slice(0, 13)); // First 13 are features
        y.push(values[13]); // Last is target (medv)
      }
    });

    return {
      X,
      y,
      feature_names: dataset.features,
      target_name: dataset.target
    };
  }

  static getDatasetCode(datasetName: string): string {
    const dataset = AVAILABLE_DATASETS.find(d => d.name === datasetName);
    if (!dataset) return '';

    return `# Load ${dataset.name} dataset
# Features: ${dataset.features.join(', ')}
# Target: ${dataset.target}
# Type: ${dataset.type}
# Size: ${dataset.size}

# Example usage:
# X, y = load_dataset('${dataset.filename}')
# print(f"Features shape: {X.shape}")
# print(f"Target shape: {y.shape}")
`;
  }
}